import React from 'react';
import { ImageDropzone } from './ImageDropzone';
import { SelectBuscable } from './SelectBuscable';
import { PRESETS_COLORES, normalizarHex, COLOR_PRIMARIO_DEFECTO, COLOR_SECUNDARIO_DEFECTO, contrasteTexto, contrasteGradiente } from '../helpers/theme';

const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1.5px solid #cbd5e1',
    marginTop: 4,
    boxSizing: 'border-box',
    fontSize: 14,
    fontFamily: "'Inter', Arial, sans-serif",
    background: 'white',
};

const labelStyle = { fontSize: 12, fontWeight: 700, color: '#475569' };

// Campo de selección de color. Se define a nivel de módulo (no dentro de
// EmpresaFormFields) para que el <input type="color"> no se remonte en cada
// cambio: si se redefine adentro, React lo desmonta/monta en cada render y el
// selector nativo se cierra al primer click o al arrastrar.
const ColorField = ({ id, label, ayuda, value, colorEsperado, onEmit }) => (
    <div>
        <label style={labelStyle}>{label}</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
            <input
                type="color"
                value={colorEsperado}
                onChange={(e) => onEmit(id, e.target.value, 'color')}
                title="Elegir color"
                style={{
                    width: 52, height: 44, padding: 2, borderRadius: 10,
                    border: '1.5px solid #cbd5e1', background: 'white',
                    cursor: 'pointer', flexShrink: 0,
                }}
            />
            <input
                type="text"
                value={value || ''}
                onChange={(e) => onEmit(id, e.target.value)}
                placeholder="#rrggbb"
                maxLength={7}
                style={{ ...inputStyle, marginTop: 0, fontFamily: 'monospace', textTransform: 'lowercase' }}
            />
        </div>
        {ayuda && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{ayuda}</div>}
    </div>
);

const sectionTitleStyle = (color = '#1e40af') => ({
    fontSize: 12,
    fontWeight: 800,
    color,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 14,
    marginTop: 10,
    borderBottom: `2px solid ${color === '#1e40af' ? '#e0e7ff' : '#d1fae5'}`,
    paddingBottom: 6,
});

// Catálogo de voces del llamado (género + acento).
// Se guarda en el campo `voz` de la entidad como 'F:es-CO' / 'M:es-CO'.
// El valor legado sin género (ej: 'es-CO') se interpreta como femenino.
export const VOCES = [
    { acento: 'es-CO', nombre: 'Español (Colombia)', emoji: '🇨🇴' },
    { acento: 'es-MX', nombre: 'Español (México)', emoji: '🇲🇽' },
    { acento: 'es-US', nombre: 'Español (Latino EE.UU.)', emoji: '🇺🇸' },
    { acento: 'es-AR', nombre: 'Español (Argentina)', emoji: '🇦🇷' },
    { acento: 'es-CL', nombre: 'Español (Chile)', emoji: '🇨🇱' },
    { acento: 'es-PE', nombre: 'Español (Perú)', emoji: '🇵🇪' },
    { acento: 'es-VE', nombre: 'Español (Venezuela)', emoji: '🇻🇪' },
    { acento: 'es-ES', nombre: 'Español (España)', emoji: '🇪🇸' },
];

export const normalizarVoz = (voz) => {
    if (!voz) return `F:${VOCES[0].acento}`;
    const s = String(voz).trim();
    if (s.includes(':')) return s;
    return `F:${s}`;
};

// Opciones aplanadas para el SelectBuscable (búsqueda por nombre, acento o género)
const opcionesVoz = [
    ...VOCES.map((v) => ({ value: `F:${v.acento}`, label: `${v.emoji} ${v.nombre} (Femenina)`, keywords: 'femenina' })),
    ...VOCES.map((v) => ({ value: `M:${v.acento}`, label: `${v.emoji} ${v.nombre} (Masculina)`, keywords: 'masculina' })),
];

export const EmpresaFormFields = ({
    form,
    onChange,
    onImageChange,
    isSuperAdmin = true,
    readonlyCodigo = false,
    accentColor = '#1e40af',
}) => {
    // Emite un cambio con la forma que esperan los consumidores (e.target.id/value/type)
    const emitir = (id, value, type = 'text') => onChange({ target: { id, value, type } });

    const colorPrimario = normalizarHex(form.colorPrimario) || COLOR_PRIMARIO_DEFECTO;
    const colorSecundario = normalizarHex(form.colorSecundario) || COLOR_SECUNDARIO_DEFECTO;

    return (
        <>
            {/* Sección 1: Datos básicos */}
            <div style={sectionTitleStyle(accentColor)}>
                Información de la Empresa
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 14 }}>
                <div>
                    <label style={labelStyle}>
                        Código Entidad{!readonlyCodigo && ' *'}
                    </label>
                    <input
                        id="codigoEntidad"
                        type="text"
                        value={form.codigoEntidad || ''}
                        onChange={readonlyCodigo ? undefined : onChange}
                        readOnly={readonlyCodigo}
                        placeholder="Ej: clinica-salud"
                        style={{
                            ...inputStyle,
                            background: readonlyCodigo ? '#f1f5f9' : 'white',
                            color: readonlyCodigo ? '#64748b' : 'inherit',
                            cursor: readonlyCodigo ? 'not-allowed' : 'text',
                        }}
                    />
                </div>
                <div>
                    <label style={labelStyle}>Nombre Empresa *</label>
                    <input
                        id="nombreEntidad"
                        type="text"
                        value={form.nombreEntidad || ''}
                        onChange={onChange}
                        required
                        placeholder="Ej: Clínica Salud Total"
                        style={inputStyle}
                    />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 14 }}>
                <div>
                    <label style={labelStyle}>NIT / Identificación</label>
                    <input id="nit" type="text" value={form.nit || ''} onChange={onChange} placeholder="Ej: 900123456-7" style={inputStyle} />
                </div>
                <div>
                    <label style={labelStyle}>Dirección</label>
                    <input id="direccion" type="text" value={form.direccion || ''} onChange={onChange} placeholder="Ej: Cra 15 # 80-20" style={inputStyle} />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 14 }}>
                <div>
                    <label style={labelStyle}>Teléfono</label>
                    <input id="telefonoContacto" type="text" value={form.telefonoContacto || ''} onChange={onChange} placeholder="Ej: 6013004000" style={inputStyle} />
                </div>
                <div>
                    <label style={labelStyle}>Email Contacto</label>
                    <input id="emailContacto" type="email" value={form.emailContacto || ''} onChange={onChange} placeholder="contacto@empresa.com" style={inputStyle} />
                </div>
                <div>
                    <label style={labelStyle}>Ciudad</label>
                    <input id="ciudad" type="text" value={form.ciudad || ''} onChange={onChange} placeholder="Ej: Bogotá" style={inputStyle} />
                </div>
            </div>

            {/* Sección 2: Imágenes */}
            <div style={sectionTitleStyle(accentColor)}>Branding / Imágenes</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 14 }}>
                <ImageDropzone
                    label="Logo de la Empresa (Header / Pantallas)"
                    value={form.imagenLogo || ''}
                    onChange={(url) => onImageChange({ imagenLogo: url })}
                    recommendedSpecs="Logo horizontal: 300x80 px | PNG transparente o SVG (máx. 2MB)"
                />
                <ImageDropzone
                    label="Ícono / Isotipo de la Empresa"
                    value={form.imagenIcono || ''}
                    onChange={(url) => onImageChange({ imagenIcono: url })}
                    recommendedSpecs="Ícono cuadrado: 128x128 px | PNG o SVG (máx. 2MB)"
                />
            </div>

            {/* Sección 2.5: Colores de la marca */}
            <div style={sectionTitleStyle(accentColor)}>Colores de la Marca</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, marginBottom: 14 }}>
                <ColorField
                    id="colorPrimario"
                    label="Color Primario (menú lateral y botones)"
                    value={form.colorPrimario || ''}
                    colorEsperado={colorPrimario}
                    ayuda="Base de la interfaz: barra lateral, botones principales y encabezados."
                    onEmit={emitir}
                />
                <ColorField
                    id="colorSecundario"
                    label="Color Secundario (acentos y degradados)"
                    value={form.colorSecundario || ''}
                    colorEsperado={colorSecundario}
                    ayuda="Se usa en acentos, degradados, ítem activo del menú y estados hover."
                    onEmit={emitir}
                />
            </div>

            {/* Presets rápidos */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                {PRESETS_COLORES.map((preset) => {
                    const activo = colorPrimario === preset.primario && colorSecundario === preset.secundario;
                    return (
                        <button
                            key={preset.nombre}
                            type="button"
                            title={preset.nombre}
                            onClick={() => {
                                emitir('colorPrimario', preset.primario, 'color');
                                emitir('colorSecundario', preset.secundario, 'color');
                            }}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '5px 10px 5px 6px', borderRadius: 999,
                                border: activo ? `2px solid ${preset.primario}` : '1.5px solid #e2e8f0',
                                background: '#fff', cursor: 'pointer', fontSize: 11.5,
                                fontWeight: 700, color: '#475569',
                            }}
                        >
                            <span style={{
                                width: 20, height: 20, borderRadius: '50%',
                                background: `linear-gradient(135deg, ${preset.primario} 0%, ${preset.secundario} 100%)`,
                                border: '2px solid #fff', boxShadow: '0 0 0 1px #e2e8f0',
                            }} />
                            {preset.nombre}
                        </button>
                    );
                })}
            </div>

            {/* Vista previa */}
            <div style={{
                display: 'flex', alignItems: 'stretch', gap: 12, flexWrap: 'wrap',
                border: '1.5px dashed #e2e8f0', borderRadius: 12, padding: 12, marginBottom: 18,
            }}>
                <div style={{
                    width: 130, borderRadius: 10, padding: '10px 12px', color: contrasteTexto(colorPrimario),
                    background: colorPrimario, display: 'flex', flexDirection: 'column', gap: 6,
                    fontSize: 11.5, fontWeight: 700,
                }}>
                    <span style={{ opacity: 0.85, fontWeight: 600 }}>Menú lateral</span>
                    <span style={{
                        background: colorSecundario, color: contrasteTexto(colorSecundario),
                        borderRadius: 8, padding: '5px 8px',
                        boxShadow: '0 2px 6px rgba(0,0,0,.2)',
                    }}>Ítem activo</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>
                        Vista previa
                    </span>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{
                            background: colorPrimario, color: contrasteTexto(colorPrimario), borderRadius: 10,
                            padding: '8px 16px', fontSize: 12.5, fontWeight: 700,
                        }}>Botón primario</span>
                        <span style={{
                            background: `linear-gradient(135deg, ${colorPrimario} 0%, ${colorSecundario} 100%)`,
                            color: contrasteGradiente(colorPrimario, colorSecundario), borderRadius: 10, padding: '8px 16px',
                            fontSize: 12.5, fontWeight: 700,
                        }}>Degradado</span>
                    </div>
                </div>
            </div>

            {/* Sección 3: Configuración */}
            <div style={sectionTitleStyle(accentColor)}>Configuración del Sistema</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 14 }}>
                <div>
                    <label style={labelStyle}>Tipo de Anuncio de Llamado</label>
                    <SelectBuscable
                        name="llamadoVoz"
                        asEvent
                        onChange={onChange}
                        isClearable={false}
                        options={[
                            { value: '0', label: '🔔 Sonido / Timbre (Gong tradicional)' },
                            { value: '1', label: '🗣️ Llamado por Voz Sintetizada' },
                        ]}
                        value={form.llamadoVoz ?? 0}
                        fontSize="14px"
                        height={44}
                    />
                </div>
                <div>
                    <label style={labelStyle}>Voz del Llamado (género y acento)</label>
                    <SelectBuscable
                        name="voz"
                        asEvent
                        onChange={onChange}
                        isClearable={false}
                        isDisabled={Number(form.llamadoVoz) === 0}
                        options={opcionesVoz}
                        value={normalizarVoz(form.voz)}
                        fontSize="14px"
                        height={44}
                    />
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                        La voz masculina se genera bajando el tono de la voz sintetizada (no es una voz masculina grabada).
                    </div>
                </div>
            </div>

            <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Mensaje Marquee (Barra inferior de pantallas de llamado)</label>
                <textarea
                    id="textoMarquee"
                    value={form.textoMarquee || ''}
                    onChange={onChange}
                    placeholder="Ej: RECUERDA: Alista los siguientes documentos para agilizar tu atención..."
                    rows={3}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
                />
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                    Mensaje informativo que se muestra en la barra animada (marquee) en las pantallas de llamado. Si está vacío, no se muestra ningún marquee.
                </div>
            </div>

            {/* Sección 4: Comercial */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 14 }}>
                <div>
                    <label style={labelStyle}>Sedes Máximas Contratadas</label>
                    <input
                        id="maxSedes"
                        type="number"
                        min="1"
                        value={form.maxSedes ?? 1}
                        onChange={onChange}
                        disabled={!isSuperAdmin}
                        style={{ ...inputStyle, fontWeight: 700, color: 'var(--color-primario-texto)', fontSize: 16, background: !isSuperAdmin ? '#f1f5f9' : 'white', cursor: !isSuperAdmin ? 'not-allowed' : 'auto' }}
                    />
                </div>
                <div>
                    <label style={labelStyle}>Estado</label>
                    <SelectBuscable
                        name="indActivo"
                        asEvent
                        onChange={onChange}
                        isClearable={false}
                        isDisabled={!isSuperAdmin}
                        options={[
                            { value: '1', label: '✅ Activa' },
                            { value: '0', label: '❌ Inactiva' },
                        ]}
                        value={form.indActivo ?? 1}
                        fontSize="14px"
                        height={44}
                    />
                </div>
            </div>

            {!isSuperAdmin && (
                <div style={{
                    background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
                    padding: '10px 14px', fontSize: 12, color: '#1e40af', marginBottom: 4,
                }}>
                    ℹ️ Los campos <b>Sedes Máximas</b> y <b>Estado</b> son administrados por el equipo de soporte técnico.
                </div>
            )}
        </>
    );
};